/**
 * User Task (User Action Item) Entity Schemas
 *
 * The normalized shape of a single open obligation a user needs to act on,
 * aggregated **at read time** across all registered sources (no stored task
 * table). Named `UserTask` deliberately — NOT `ActionItem` — because the
 * `ACTION_ITEM` ContentType is just one (future) source feeding this shape,
 * while `UserTask` is the aggregation surface several sources feed.
 *
 * These are USER tasks: the base case is a user's own obligations (trainings,
 * acknowledgements, compliance, signatures); a parent acting for a child is one
 * delegated-subject case, not the only one.
 *
 * v1 ships the framework dark: the production resolver registry is empty, so
 * GET /v1/user-tasks returns `{ tasks: [], count: 0 }` until a real source
 * registers. See docs/_archive/2026-10-17-plans-sweep/plans/2026-05-25-002-feat-parent-action-items-plan.md.
 */

import { z } from 'zod'

/**
 * Resolution mode — the core of what makes a thing an action item.
 *  - `auto`:   the source has a done-state the resolver queries, so it only
 *              ever returns OPEN items; a completed obligation simply isn't
 *              returned (nothing stored on our side).
 *  - `manual`: the source can't report completion, so the user marks it done
 *              and we record `completedAt` per-user; the aggregator anti-joins
 *              completed items. Load-bearing for sources without a done-state
 *              (most Blackbaud obligations).
 */
export const UserTaskResolutionSchema = z.enum(['auto', 'manual'])
export type UserTaskResolution = z.infer<typeof UserTaskResolutionSchema>

/**
 * Provenance — where the obligation originates. `in_app` items are owned by our
 * platform; `external` items point out to another system (e.g. Blackbaud) the
 * user completes elsewhere.
 */
export const UserTaskProvenanceSchema = z.enum(['in_app', 'external'])
export type UserTaskProvenance = z.infer<typeof UserTaskProvenanceSchema>

/**
 * Urgency band — a coarse, source-independent priority used for tie-breaking in
 * the ranked list (after `dueAt`). Not a due date; a source with no due date
 * can still flag an item urgent.
 */
export const UserTaskUrgencySchema = z.enum(['low', 'normal', 'high'])
export type UserTaskUrgency = z.infer<typeof UserTaskUrgencySchema>

/**
 * Which subject this obligation is for — the acting user themselves or a
 * delegated subject they act for (e.g. a child). Nullable on the task itself: a
 * broadcast targets a segment (no specific subject), a real per-subject
 * obligation carries exactly one.
 */
export const UserTaskSubjectSchema = z.object({
  /** UsersOnSchools.publicId of the subject (self or delegated). */
  subjectPublicId: z.string(),
  /** Display label for the subject (preferred/first name; "You" for self). */
  label: z.string()
})
export type UserTaskSubject = z.infer<typeof UserTaskSubjectSchema>

/**
 * Call-to-action — what the user does to resolve the obligation, and where.
 * `target` is an app-relative route or external URL; `verb` is a short label
 * for the action button ("Sign", "RSVP", "Review").
 */
export const UserTaskCtaSchema = z.object({
  target: z.string(),
  verb: z.string()
})
export type UserTaskCta = z.infer<typeof UserTaskCtaSchema>

/**
 * The requesting user's own interaction state with this task (from the shared
 * UserItemInteraction store). All three are independent timestamps.
 *
 * NOTE on the LIST response: `dismissedAt` and `completedAt` are ALWAYS null on
 * items returned by `GET /v1/user-tasks` — a dismissed or completed item is
 * filtered out by the aggregator's anti-join, so a returned item is by
 * definition neither. They are populated (non-null) only on the dismiss /
 * complete endpoint responses. `seenAt` may be non-null on list items.
 */
export const UserTaskRequesterStateSchema = z.object({
  dismissedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  seenAt: z.string().datetime().nullable()
})
export type UserTaskRequesterState = z.infer<typeof UserTaskRequesterStateSchema>

/**
 * A single normalized open obligation. `stableKey` is the dedup + dismiss +
 * complete key (same key across all three): `<source>:<id>` for a broadcast,
 * `<source>:<id>:subject:<subjectPublicId>` for a per-subject obligation.
 */
export const UserTaskSchema = z.object({
  stableKey: z.string(),
  /** Source domain that emitted this obligation (e.g. "field_trip_form"). */
  sourceType: z.string(),
  /** Opaque source-side reference (e.g. the source record publicId). */
  sourceRef: z.string(),
  resolution: UserTaskResolutionSchema,
  /** Null for a broadcast (segment-targeted); set for a per-subject obligation. */
  subject: UserTaskSubjectSchema.nullable(),
  title: z.string(),
  body: z.string().nullable(),
  /** When the obligation is due, if the source knows. Null = no deadline. */
  dueAt: z.string().datetime().nullable(),
  urgency: UserTaskUrgencySchema,
  provenance: UserTaskProvenanceSchema,
  cta: UserTaskCtaSchema,
  /** Whether the surface should offer a "dismiss" affordance for this item. */
  dismissible: z.boolean(),
  requesterState: UserTaskRequesterStateSchema
})
export type UserTask = z.infer<typeof UserTaskSchema>
